import React from "react";
import PropTypes from "prop-types";
import UnderlineInput from "../common/UnderlineInput";
import SelectDropdown from "../common/SelectDropdown";
import { countryOptions } from "../../../../../helpers/isoCountries";

export const PAYMENT_TYPES = {
  LTR: "LTR · Domestic transfer (CITAD)",
  RTP: "RTP · Domestic real-time 24/7 (NAPAS)",
  FTR: "FTR · Overseas / foreign currency",
};
const CHARGES = { SHAR: "SHAR · Each side pays its own bank", DEBT: "DEBT · Pebsteel pays all", CRED: "CRED · Recipient pays all" };

// A BIC carries the bank's country in characters 5–6: VTCBVNVX → VN, ABOCCNBJ100 → CN.
const BIC = /^[A-Z]{4}([A-Z]{2})[A-Z0-9]{2}([A-Z0-9]{3})?$/;
export const bicCountry = (bic) => BIC.exec(String(bic || "").trim().toUpperCase())?.[1] || "";

// Recipient country unless Accounting picked one: the BIC's country, else VN.
export const recipientCountry = (payment) =>
  String(payment.recipient_country || bicCountry(payment.bank_bic) || "VN").toUpperCase();
const countrySource = (payment) => (payment.recipient_country ? "" : bicCountry(payment.bank_bic) ? " (from BIC)" : "");

// The payment type the file will carry: the one chosen, else what the server would pick —
// FTR for a foreign currency or an overseas recipient, the domestic default otherwise.
export const effectivePaymentType = (payment, v3, currency) => {
  if (PAYMENT_TYPES[payment.payment_type]) return payment.payment_type;
  if ((currency || "VND") !== "VND" || recipientCountry(payment) !== "VN") return "FTR";
  return ["LTR", "RTP"].includes(v3?.payment_type) ? v3.payment_type : "LTR";
};

// HSBC XMLv3 fields still missing for this payment type (shown with the other details).
export const missingXmlV3 = (payment, type) => {
  const blank = (key) => !String(payment[key] || "").trim();
  if (type === "FTR") return blank("recipient_town") ? ["Recipient town"] : [];
  return blank("bank_clearing_code") ? ["Bank code (CITAD / NAPAS)"] : [];
};

// Data that is there but cannot go with this payment type — mirrors XmlV3#invalid:
// LTR (CITAD) / RTP (NAPAS) only reach banks in Vietnam.
export const invalidXmlV3 = (payment, type) => {
  if (type === "FTR") return [];
  const abroad = [...new Set([recipientCountry(payment), payment.bank_country || bicCountry(payment.bank_bic)]
    .map((c) => String(c || "VN").toUpperCase()).filter((c) => c !== "VN"))];
  return abroad.length ? [`${type} is a domestic payment — recipient in ${abroad.join("/")} needs FTR.`] : [];
};

/**
 * HSBC XMLv3 (pain.001) details of the payment — like HSBC Vietnam's FTR / LTR / RTP
 * samples and the live IFILE: payment type, the bank's domestic code, who pays the
 * charges and the recipient's address. Trade loan sits in PaymentDetailsFields.
 */
const HsbcXmlV3Fields = ({ payment, onChange, v3, currency, disabled }) => {
  const type = effectivePaymentType(payment, v3, currency);
  const local = type !== "FTR";
  const set = (key) => (value) => onChange((p) => ({ ...p, [key]: value }));
  const input = (key, label, { required, maxLength, upper, placeholder } = {}) => (
    <label className="tw-block" data-missing={(required && !String(payment[key] || "").trim()) || undefined}>
      <span className="payment-journal-setup-label">{label}{required && <span className="tw-text-[#b42318]"> *</span>}</span>
      <UnderlineInput aria-label={`HSBC ${label}`} value={payment[key] || ""} disabled={disabled} maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => set(key)(upper ? e.target.value.toUpperCase() : e.target.value)} />
    </label>
  );

  const country = (key, label, fallback) => (
    <label className="tw-block">
      <span className="payment-journal-setup-label">{label}</span>
      <SelectDropdown aria-label={`HSBC ${label}`} value={payment[key] || ""} disabled={disabled}
        options={countryOptions()} allowClear clearLabel={`Default · ${fallback}`} placeholder={`Default · ${fallback}`}
        onChange={(value) => set(key)(value || "")} />
    </label>
  );
  const invalid = invalidXmlV3(payment, type);

  return (
    <div aria-label="HSBC XMLv3 details" className="tw-mt-3 tw-flex tw-flex-col tw-gap-3">
      <div className="payment-journal-payment-fields">
        <label className="tw-block">
          <span className="payment-journal-setup-label">Payment type</span>
          <SelectDropdown aria-label="HSBC Payment type" value={payment.payment_type || ""} disabled={disabled}
            options={[{ value: "", label: `Auto · ${PAYMENT_TYPES[type]}` },
              ...Object.entries(PAYMENT_TYPES).map(([value, label]) => ({ value, label }))]}
            onChange={(value) => set("payment_type")(value || "")} />
        </label>
        {local
          ? input("bank_clearing_code", "Bank code (CITAD / NAPAS)", { required: true, maxLength: 35, upper: true, placeholder: "e.g. 01201001" })
          : country("bank_country", "Recipient bank country",
            bicCountry(payment.bank_bic) ? `${bicCountry(payment.bank_bic)} (from BIC)` : `same as recipient (${recipientCountry(payment)})`)}
        <label className="tw-block">
          <span className="payment-journal-setup-label">Charges (ChrgBr)</span>
          <SelectDropdown aria-label="HSBC Charges" value={payment.charge_bearer || ""} disabled={disabled}
            options={[{ value: "", label: `Default · ${v3?.charge_bearer && !payment.charge_bearer ? v3.charge_bearer : "from Settings"}` },
              ...Object.entries(CHARGES).map(([value, label]) => ({ value, label }))]}
            onChange={(value) => set("charge_bearer")(value || "")} />
        </label>
      </div>

      <div>
        <p className="tw-m-0 tw-mb-1.5 tw-text-[12px] tw-font-semibold tw-text-geist-fg-2">Recipient address</p>
        <div className="payment-journal-payment-fields">
          {input("recipient_street", "Street", { maxLength: 70 })}
          {input("recipient_building", "Building / lot", { maxLength: 16 })}
          {input("recipient_postcode", "Postcode", { maxLength: 16 })}
          {input("recipient_town", "Town / city", { required: !local, maxLength: 35 })}
          {country("recipient_country", "Country", `${recipientCountry(payment)}${countrySource(payment)}`)}
        </div>
      </div>

      {invalid.length > 0 && (
        <p role="alert" className="tw-m-0 tw-text-[12px] tw-text-[#b42318]">{invalid.join(" ")}</p>
      )}
    </div>
  );
};

HsbcXmlV3Fields.propTypes = {
  payment: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  // Server state: { enabled, payment_type, charge_bearer, … } — see journal_workbench hsbc_xml_v3.
  v3: PropTypes.object,
  currency: PropTypes.string,
  disabled: PropTypes.bool,
};

export default HsbcXmlV3Fields;
